import { useRef, useState } from "react";
import { Link } from "./Navigation";
import { ArrowUpRight, AudioLines, Check } from "lucide-react";
import { usePageSeoFor } from "@/lib/seo";
import { PAGE_SEO } from "@/data/pageSeo";
import { VoiceAIOrb } from "@/components/VoiceAIOrb";
import { VERTICALS } from "@/data/verticals";
import { industryPhoto } from "@/data/industryPhotos";
import { PLANS, money } from "./commerce";
import { HOME_PLAN_SCOPE } from "@/data/homePlanScope";

import { Shell, PHONE } from "./Shell";
import "./rebuild.css";
import { CallToBooking } from "./CallToBooking";
import { SetUpOnce } from "./SetUpOnce";
import { ScenicHero } from "./ScenicHero";
import { useScrollEngine } from "./useScrollEngine";
import { Calculator } from "./Calculator";
import { HomeClose } from "./HomeClose";
import "./cinematic.css";
import "./fora.css";
import "./scroll-acts.css";
import "./home-glass.css";
import "./home-rest.css";

// The signature move: the voice section carries a trace rail along its bottom
// edge whose playhead is scroll position. The bar heights are a fixed figure,
// not a recording and not random, and the rail is decorative (aria-hidden):
// nothing here claims audio is playing.
const WAVE_STEPS =
  typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
    ? 60
    : 120;
const WAVE = Array.from({ length: WAVE_STEPS }, (_, index) => {
  const i = index * (120 / WAVE_STEPS);
  return Number(
    (
      0.22 +
      0.36 * Math.abs(Math.sin(i * 0.7)) +
      0.3 * Math.abs(Math.sin(i * 0.23 + 1.1))
    ).toFixed(3),
  );
});

export function VoiceDemo() {
  return (
    <section
      className="rb-demo-band g-tone g-tone--lift"
      id="live-demo"
      data-sc-act="flow"
    >
      <div className="rb-demo rb-wrap">
        <div className="rb-demo-copy" data-sc-in data-sc-stagger="70">
          <h2>Hear it answer a real call.</h2>
          <p>
            This is the same agent that answers our phone. Ask it about Chleb,
            ask about pricing, or ask how it would handle your business.
          </p>
          <a className="rb-text-link" href={PHONE}>
            Or call (813) 694-8671 <ArrowUpRight size={17} />
          </a>
        </div>
        <div
          className="rb-voice-surface g-panel g-panel--strong g-panel--flush"
          data-sc-in
        >
          <div className="g-panel-head">
            <span className="fo-view-title">
              <AudioLines size={15} /> Voice demo
            </span>
            {/* Every other panel on this page is labelled Illustrative. This
                one is the exception and says so: the orb dials the agent that
                answers our own phone. */}
            <span className="g-panel-label">Live agent</span>
          </div>
          <div className="rb-voice-body">
            <VoiceAIOrb />
            <strong>Call the agent</strong>
            <p>Use your microphone. End the call anytime.</p>
          </div>
        </div>
      </div>
      <div className="rb-wave" aria-hidden="true">
        {WAVE.map((h, i) => (
          <i
            key={i}
            style={
              {
                "--h": h,
                "--at": (i / (WAVE.length - 1)).toFixed(3),
              } as React.CSSProperties
            }
          />
        ))}
        <u />
      </div>
    </section>
  );
}

// The rail's one line per trade is the caller's own opening turn from the
// sample call on that industry's page, not a note written for the tile: the
// two can never drift apart.
const RAIL_LINE = (slug: string) =>
  VERTICALS.find((v) => v.slug === slug)?.sampleCall.transcript[0]?.text ?? "";

export default function Home() {
  const seo = PAGE_SEO["/"];
  usePageSeoFor("/");
  // The scroll acts are desktop-with-motion only. Everything below renders the
  // same words either way, so the phone, reduced motion and the no-JS
  // prerender all get complete static sections instead of a stalled stage.
  const [enhanced] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(
        "(min-width: 768px) and (prefers-reduced-motion: no-preference)",
      ).matches,
  );
  // This page carried its own copy of the mount, which is how it ended up
  // being the only one of the three without the deferred-script retry and
  // without the phone reveal. It is the shared hook now: same layout-effect
  // timing, same html.sc-ready contract, plus the load fallback and the
  // IntersectionObserver reveal that phones get instead of the engine.
  useScrollEngine();
  // A panned rail holds one viewport position for its whole act, so the
  // browser's own scroll-into-view cannot reach a tile that has not travelled
  // yet: it has to park the act at the progress that brings the tile on
  // screen. Only the page knows that mapping, so it is written here
  // (scroll-craft verify.md, "Keyboard").
  const railSection = useRef<HTMLElement>(null);
  const rail = useRef<HTMLDivElement>(null);
  const reachRailItem = (event: React.FocusEvent) => {
    const section = railSection.current;
    const track = rail.current;
    const item = event.target;
    if (!enhanced || !section || !track || !(item instanceof HTMLElement))
      return;
    const stage = section.firstElementChild as HTMLElement | null;
    if (!stage) return;
    const over = track.scrollWidth - window.innerWidth;
    if (over <= 0) return;
    const distance = over * 1.05;
    const wanted =
      (item.offsetLeft + item.offsetWidth / 2 - window.innerWidth / 2) /
      distance;
    const travel = Math.max(1, section.offsetHeight - stage.offsetHeight);
    const top =
      section.getBoundingClientRect().top +
      window.scrollY +
      travel * Math.max(0, Math.min(1, wanted));
    window.scrollTo({ top, behavior: "instant" });
  };
  // Span 2.6: with the lead out of the rail it overflows the viewport by
  // 1,376px at 1440 and 1,604px at 1100 (measured against scrollWidth), and
  // the act travels 1.6 viewport heights, so the rail moves at about the speed
  // of the wheel. The peak still keeps the most scroll room on the page, but
  // it is no longer an engine act: the deck in SetUpOnce.tsx buys its own
  // track out of its four sticky cards, their gaps and the hold at the end
  // (the --deck-* tokens in setuponce.css).
  const pan = enhanced
    ? ({ "data-sc-act": "pan", "data-sc-span": "2.6" } as const)
    : {};
  return (
    <Shell>
      <ScenicHero />
      {/* The plain answer to "what is this company", in one paragraph, right
          where the hero hands over. The hero's own words are the operator's
          and are fixed by GOAL.md, so the answer-first prose lives here
          instead. scripts/prerender.cjs stamps this same heading and this same
          paragraph into the static homepage, from the same
          src/data/pageSeo.ts string, so a crawler and a reader get one answer
          rather than two. */}
      <section
        className="rb-answer g-tone g-tone--warm"
        aria-labelledby="what-chleb-ai-does"
        data-sc-act="flow"
      >
        {/* The only section on the page that arrived with no arrival at all:
            everything below it rises in on the engine's stagger and this one
            was simply there. The engine's reveal is gated on html.sc-ready and
            drops to a 220ms fade under prefers-reduced-motion, and the
            prerendered copy of this heading is emitted separately by
            scripts/prerender.cjs, so a crawler still reads it either way. */}
        {/* No photograph here. The one that stood in this row was the only
            cold interior on a page whose other four stills are warm and
            shallow, and it told the same beat twice: CallToBooking directly
            below walks through a call arriving and being answered, and
            VoiceDemo makes the case a third time. The section is the page's
            plain answer, so it is left as the answer. */}
        <div className="rb-answer-layout rb-wrap" data-sc-in data-sc-stagger="70">
          <div className="rb-answer-copy">
            <h2 id="what-chleb-ai-does">What does Chleb AI do?</h2>
            <p>{seo.intro}</p>
          </div>
        </div>
      </section>
      <CallToBooking />
      <SetUpOnce />
      <VoiceDemo />
      {/* The page's one pricing block. It used to sit below the industries
          rail with a second, older price list (the product ledger) up here;
          two pricing blocks on one page is one too many, so the ledger is
          gone and this took its slot between the voice band and the
          calculator. It carries the same three prices the ledger did, plus
          the annual checkout the ledger had no room for. */}
      <section
        className="hp g-tone g-tone--warm"
        id="plans-preview"
        data-sc-act="flow"
      >
        <div className="rb-wrap">
          <div className="hp-head" data-sc-in data-sc-stagger="60">
            <span className="g-eyebrow">Pricing</span>
            <h2>Three ways in. One front office.</h2>
            <p className="g-lede">
              Every plan includes missed-call recovery. Usage is billed
              separately at carrier cost, with no Chleb markup.
            </p>
          </div>
          <div className="hp-cards" data-sc-in data-sc-stagger="60">
            {PLANS.map((plan) => (
              <article
                key={plan.id}
                className={
                  "hp-card g-panel g-panel--strong" +
                  (plan.id === "receptionist" ? " is-raised" : "")
                }
              >
                <span className="g-panel-label">{plan.name}</span>
                <p className="hp-price">
                  <strong>{money(plan.monthly)}</strong>
                  <span>/month</span>
                </p>
                <p className="hp-intro">{plan.intro}</p>
                <p className="hp-intro">{HOME_PLAN_SCOPE[plan.id]}</p>
                <a
                  className={
                    plan.id === "receptionist" ? "g-btn" : "g-btn g-btn--ghost"
                  }
                  href={plan.monthlyUrl}
                  aria-label={`Ask about ${plan.name}`}
                >
                  Ask about this plan
                </a>
                <ul className="hp-features">
                  {plan.features.slice(0, 4).map((f) => (
                    <li key={f}>
                      <span className="g-check">
                        <Check size={12} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <p className="hp-annual">
            Interested in annual billing? Ask about{" "}
            {PLANS.map((plan, i) => (
              <span key={plan.id}>
                {i > 0 ? (i === PLANS.length - 1 ? " or " : ", ") : ""}
                <a href={plan.annualUrl}>{plan.name}</a>
              </span>
            ))}
            . <Link to="/pricing">See the full comparison</Link>
          </p>
        </div>
      </section>
      <Calculator />
      <section
        className="ch-industry-index"
        id="industries-rail"
        ref={railSection}
        aria-labelledby="industries-title"
        {...pan}
      >
        <div
          className="rb-industry-stage g-band g-band--midday"
          {...(enhanced ? { "data-sc-stage": "" } : {})}
        >
          {/* The lead sits in the stage, not in the rail: a panned act holds
              one viewport for two and a half screens, and a heading that
              travels off with the first tile leaves the rest of the act
              anonymous. It stays; the tiles move under it. */}
          <div className="rb-wrap rb-rail-lead">
            <h2 id="industries-title">Find your industry.</h2>
            <p>
              The call, the questions and the handoff are configured per trade,
              not written once and reused.
            </p>
          </div>
          <div
            className="rb-industry-rail"
            ref={rail}
            onFocusCapture={reachRailItem}
            {...(enhanced ? { "data-sc-pan": "0.05" } : {})}
          >
            {VERTICALS.map((v, i) => {
              const photo = industryPhoto(v.slug);
              return (
                <Link
                  className="g-panel g-panel--strong rb-rail-tile"
                  to={v.route}
                  key={v.slug}
                  style={{ "--i": i } as React.CSSProperties}
                >
                  {photo && (
                    <span className="g-photo rb-rail-photo">
                      <img
                        src={photo.srcSmall}
                        width={photo.width}
                        height={photo.height}
                        alt={photo.alt}
                        loading="lazy"
                        decoding="async"
                      />
                    </span>
                  )}
                  <span className="g-panel-label">Sample call</span>
                  <strong>{v.vertical}</strong>
                  <q>{RAIL_LINE(v.slug)}</q>
                  <span className="g-arrow" aria-hidden="true">
                    <ArrowUpRight size={18} />
                  </span>
                  <span className="g-sr">Explore Chleb for {v.vertical}</span>
                </Link>
              );
            })}
            <div className="rb-rail-end">
              <p>Not on the list? The setup starts from your own call flow.</p>
              <Link className="rb-text-link" to="/industries">
                See every industry <ArrowUpRight size={17} />
              </Link>
            </div>
          </div>
        </div>
      </section>
      <HomeClose />
    </Shell>
  );
}
